import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'

/**
 * Découpe la valeur écrite par l'auteur, et déduit la façon dont il l'a MISE EN
 * FORME — c'est elle qu'on reproduira à chaque image de l'animation.
 *
 * Trois morceaux : ce qui précède le premier chiffre, le nombre, ce qui suit le
 * dernier. Le séparateur de milliers n'est pas deviné par une locale : c'est
 * CELUI QUE L'AUTEUR A TAPÉ, reconnu au fait qu'il découpe le nombre en tranches
 * de trois après la première (« 2 571 », « 80 416 », « 1,234 »). Tout autre
 * caractère entre deux chiffres est une décimale (« 1.2 », « 3,14 ») : le compte
 * à rebours n'a pas de sens dessus, on rend la valeur telle quelle.
 *
 * @returns {{prefix: string, suffix: string, value: number, separator: string|null}|null}
 *   NULL quand il n'y a rien à animer.
 */
export const parseCountTarget = (raw) => {
  const text = String(raw ?? '')
  const first = text.search(/\d/)
  if (first === -1) return null

  let last = first
  for (let i = text.length - 1; i >= first; i--) {
    if (text[i] >= '0' && text[i] <= '9') { last = i; break }
  }

  const core = text.slice(first, last + 1)
  const separators = core.match(/\D/g) ?? []
  const groups = core.split(/\D/)
  const uniform = separators.length > 0 && separators.every((s) => s === separators[0])
  const grouped = uniform && groups.slice(1).every((g) => g.length === 3)

  // Une décimale n'est pas un groupement : « 1.2 » deviendrait « 12. ».
  if (separators.length > 0 && !grouped) return null

  const value = parseInt(groups.join(''), 10)
  if (!Number.isFinite(value)) return null

  return {
    prefix: text.slice(0, first),
    suffix: text.slice(last + 1),
    value,
    separator: grouped ? separators[0] : null,
  }
}

/** Rend un entier dans la mise en forme relevée sur la cible. */
export const formatCount = (parsed, n) => {
  const digits = parsed.separator
    ? String(n).replace(/\B(?=(\d{3})+(?!\d))/g, parsed.separator)
    : String(n)
  return `${parsed.prefix}${digits}${parsed.suffix}`
}

/**
 * Hook — anime un nombre de 0 jusqu'à sa cible quand l'élément entre dans le
 * cadre. Deux garde-fous que l'absence d'animation NE DOIT PAS transformer en
 * perte d'information :
 *
 *  - l'affichage part de la VALEUR FINALE, jamais de « 0 ». Sans JavaScript, en
 *    génération statique (où l'observateur est un leurre qui ne se déclenche
 *    jamais) ou sur un navigateur sans `IntersectionObserver`, la page montre le
 *    vrai chiffre au lieu d'un zéro figé ;
 *  - `prefers-reduced-motion` coupe l'animation : le chiffre s'affiche, point.
 */
const useCountUp = (target, duration = 1800) => {
  const parsed = useMemo(() => parseCountTarget(target), [target])
  const finalText = parsed ? formatCount(parsed, parsed.value) : String(target ?? '')
  const [display, setDisplay] = useState(finalText)
  const ref = useRef(null)
  const animated = useRef(false)

  useEffect(() => {
    // Le verrou se relâche à chaque changement de cible : le composant est
    // recyclé par l'itération d'un Switch quand les chiffres sont modifiés, et
    // la référence resterait sinon à `true` depuis la première animation.
    animated.current = false
    setDisplay(finalText)
    if (!parsed) return

    const reduced = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || animated.current) return
        animated.current = true
        const start = performance.now()
        const tick = (now) => {
          const progress = Math.min((now - start) / duration, 1)
          const eased = 1 - Math.pow(1 - progress, 3)
          setDisplay(formatCount(parsed, Math.round(eased * parsed.value)))
          if (progress < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
        observer.disconnect()
      },
      { threshold: 0.3 },
    )

    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [parsed, finalText, duration])

  return { display, ref }
}

/**
 * ReactiveJSON primitive — renders a number that animates from 0 to its
 * target value when it enters the viewport.
 *
 * La mise en forme de la cible est PRÉSERVÉE pendant tout le décompte : le
 * séparateur de milliers écrit par l'auteur (« 2 571 ») est reposé à chaque
 * image, et le suffixe (« 20+ ») reste à sa place.
 *
 * YAML usage:
 *   - type: CountUp
 *     value: ~.value           # "2 571" | "80 416" | "20+" | "50"
 *     duration: 1800           # optional, ms
 *     tag: span                # optional, default "span"
 *     attributes:
 *       class: "text-5xl font-bold tabular-nums"
 *
 * `tabular-nums` n'est pas un détail d'esthétique : sans lui, la largeur du
 * nombre saute à chaque image et pousse ce qui l'entoure.
 */
export const CountUp = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const value = resolve(props.value)
  const duration = resolve(props.duration) ?? 1800
  const evaluatedAttrs = useEvaluatedAttributes(props.attributes ?? {})

  const { display, ref } = useCountUp(value, duration)

  const Tag = props.tag ?? 'span'

  return (
    <ActionDependant {...props}>
      <Tag ref={ref} {...evaluatedAttrs}>{display}</Tag>
    </ActionDependant>
  )
}
